/* eslint-disable max-lines */
import { Injectable, inject } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { Router } from '@angular/router';
import {
  LoginAuthUserInputFields,
  LoginAuthenticationScreen,
  LoginFormValidate
} from '@com-delta-idp/mnx-login-ui';
import {
  LoginRememberMeUtilsService,
  OauthAEMConfig,
  RememberMeWrapperService
} from '@com-delta-idp/mnx-oauth';
import {
  AnalyticsService,
  CommonService,
  UserSessionService
} from '@com-delta-idp/mnx-sitewide-shared-core-smart';
import {
  CookieHelperService,
  IropService,
  LoggingService
} from '@com-delta-idp/mnx-utils';
import { BehaviorSubject } from 'rxjs';
import { CoreUtilService } from '../../utils/core-util.service';
import { LoginAuthenticationHelperService } from '../../utils/login-authentication-helper.service';
import { LoginHelperUtils } from '../../utils/login-helper.utils';
import { NuTravelSkybonusUtilService } from '../../utils/nuTravelSkyBonusUtils/nu-travel-skybonus-util.service';
import { LoginContentAdapterService } from '../adapters/login-content-adapter.service';
import { PingLoginAdapterService } from '../adapters/ping-login-adapter.service';
import { DATALAYER_PAGE_EVENTS } from '../constants/analytics-constants';
import {
  LOGIN_PAGE_ROUTES,
  STANDALONE_PAGE_URL,
  VIEW_NAME
} from '../constants/login-constant';
import { LoginModalContent } from '../models/aem';
import { loginCustomerResponse } from '../models/login-authentication-screen.interface';
import {
  LoginContentService,
  LoginModalHelperService,
  NextDisplayViewService
} from '../services';
import { UpdateDataLayerService } from '../services/analytics/update-data-layer.service';
import { FlowRouterService } from '../services/flow-router.service';
import { LoginDasboardService } from '../services/login-dasboard.service';
import { LoginResponseService } from '../services/login-response.service';

@Injectable({
  providedIn: 'root'
})
export class FacadeSupportService {
  loginContent$: BehaviorSubject<LoginAuthenticationScreen | undefined> =
    new BehaviorSubject<LoginAuthenticationScreen | undefined>(undefined);

  loginAuthenticationScreenContent: LoginAuthenticationScreen =
    {} as LoginAuthenticationScreen;

  loader$: BehaviorSubject<boolean | undefined> = new BehaviorSubject<
    boolean | undefined
  >(false);

  authenticationConfig!: OauthAEMConfig;
  fromApplication: string | undefined;
  aemContent!: LoginModalContent;
  standalonePage = false;
  redirectUrl: string | undefined;
  errorAdobeTracker$ = new BehaviorSubject(false);

  currentUserInputFields?: LoginAuthUserInputFields;
  flowIdForTheTransaction!: string | undefined;

  protected helperService = inject(LoginModalHelperService);
  protected contentService = inject(LoginContentService);
  protected iropService = inject(IropService);
  protected adapter = inject(LoginContentAdapterService);
  protected pingAdapter = inject(PingLoginAdapterService);
  protected loginAuthenticationHelperService = inject(
    LoginAuthenticationHelperService
  );

  protected analyticsService = inject(AnalyticsService);

  protected nextDisplayViewService = inject(NextDisplayViewService);
  protected loginHelperUtils = inject(LoginHelperUtils);
  protected loginDasboardService = inject(LoginDasboardService);
  protected dataLayerServcie = inject(UpdateDataLayerService);
  protected loginResponseService = inject(LoginResponseService);
  protected router = inject(Router);
  protected coreUtilService = inject(CoreUtilService);
  protected common = inject(CommonService);
  protected cookieHelper = inject(CookieHelperService);
  protected loginServiceWoker = inject(UserSessionService);
  protected nuTravelSkyBonusUtilService = inject(NuTravelSkybonusUtilService);
  protected flowRouter = inject(FlowRouterService);
  protected loginRememberMeUtils = inject(LoginRememberMeUtilsService);
  protected loggingService = inject(LoggingService);
  protected rememberMeWrapperService = inject(RememberMeWrapperService);

  getContent(): LoginAuthenticationScreen {
    return this.loginContent$.value || ({} as LoginAuthenticationScreen);
  }

  focusChange(fieldName: string) {
    LoginFormValidate.touchControl(fieldName, this.getContent());
  }

  resetForm() {
    if (this.getContent().loginFormAttributes) {
      this.getContent().loginFormAttributes.submitAttempt = false;
      this.getContent().loginFormAttributes.userIdControlTouched = false;
      this.getContent().loginFormAttributes.passwordControlTouched = false;
      this.getContent().loginFormAttributes.lastNameControlTouched = false;
      this.getContent().loginFormAttributes.keepMeLoggedInTouched = false;
      this.loginAuthenticationScreenContent?.formGroup?.reset();
      this.getContent().loginFormAttributes.isLastNameRequired = false;
      this.getContent().loginFormAttributes.formAlertSuccessIcon = false;
      this.getContent()
        .formGroup?.get('keepMeLoggedIn')
        ?.reset({ value: false });
      const lastNameControl = this.getContent().formGroup.get('lastName');
      lastNameControl?.disable();
    }
  }

  updateLoaderAndDisableButton(updateValue: boolean): void {
    this.getContent().disableButton = updateValue;
    this.loader$.next(updateValue);
  }

  updateErrorCountAndDisplayAlertBanner() {
    this.getContent().loginFormAttributes.errorCount =
      LoginFormValidate.validateFormFields(this.getContent().formGroup);
    this.getContent().loginFormAttributes.errorAlertBanner =
      LoginFormValidate.getErrorAlert(this.getContent().formGroup);
  }

  checkEmail(isEmailAllowed: boolean | undefined, data: { value: string }) {
    return (
      (isEmailAllowed && this.helperService.isEmailEntered(data?.value)) ||
      (!isEmailAllowed && this.helperService.isUserNameEntered(data?.value))
    );
  }

  checkLastName(isEmailAllowed: boolean | undefined) {
    const userId = <UntypedFormControl>(
      this.getContent().formGroup.get('userId')
    );
    const lastNameControl = this.getContent().formGroup.get('lastName');
    lastNameControl?.disable();
    userId?.valueChanges.subscribe((data) => {
      if (data) {
        if (
          this.checkEmail(isEmailAllowed, data) &&
          !this.authenticationConfig?.NO_LAST_NAME
        ) {
          lastNameControl?.enable();
          this.getContent().loginFormAttributes.isLastNameRequired = true;
        } else {
          lastNameControl?.disable();
          this.getContent().loginFormAttributes.isLastNameRequired = false;
        }
      } else {
        lastNameControl?.disable();
        this.getContent().loginFormAttributes.isLastNameRequired = false;
      }
    });
  }

  formValueChange(): void {
    this.getContent().formGroup.valueChanges.subscribe(() => {
      this.getContent().loginFormAttributes.errorCount =
        LoginFormValidate.validateFormFields(this.getContent().formGroup);
      this.getContent().loginFormAttributes.errorAlertBanner =
        LoginFormValidate.getErrorAlert(this.getContent().formGroup);
    });
  }

  formAlertDisplay() {
    if (this.getContent() && this.getContent().loginFormAttributes) {
      this.getContent().errorCode = undefined;
      this.getContent().loginFormAttributes.formAlertSuccessIcon = false;
    }
  }

  displayPingError(tokenResponse: {
    errorId: string;
    accountLocked?: boolean;
  }) {
    const errorMessageFromAem: string | undefined =
      this.loginAuthenticationHelperService.getErrorMessageFromAem(
        this.aemContent,
        tokenResponse?.errorId
      );
    this.loginAuthenticationScreenContent = {
      ...this.loginAuthenticationScreenContent,
      errorCode: tokenResponse?.errorId,
      errorMessage: errorMessageFromAem
    };
    this.resetForm();
    this.dataLayerServcie.triggerLoginDataLayerEvents(
      DATALAYER_PAGE_EVENTS.LOGIN_UNSUCCESSFUL
    );
    this.dataLayerServcie.updateErrorDetails(
      tokenResponse?.errorId,
      errorMessageFromAem
    );
    this.loginContent$.next(this.loginAuthenticationScreenContent);
    if (tokenResponse?.accountLocked) {
      this.dataLayerServcie.triggerLoginDataLayerEvents(
        DATALAYER_PAGE_EVENTS.LOGIN_UNLOCK_ACCOUNT
      );
      if (!this.standalonePage) {
        this.nextDisplayViewService.nextDisplayView(
          VIEW_NAME.UNLOCK_ACCOUNT_SCREEN
        );
      } else {
        this.router.navigateByUrl(
          `/${STANDALONE_PAGE_URL[VIEW_NAME.UNLOCK_ACCOUNT_SCREEN]}`
        );
      }
    }
  }

  setRmeUI(rememberMe: string | undefined) {
    if (rememberMe === 'Y') {
      this.coreUtilService.setLocalStorageWithExpiry(
        'rme-ui',
        true,
        94608000000
      );
    }
  }

  custLoginBffCall(
    userInputFields: LoginAuthUserInputFields | undefined,
    queryParam: string,
    standalonePage?: boolean
  ) {
    if (userInputFields?.userName && userInputFields?.password) {
      this.loginDasboardService
        .custLogin(
          this.adapter.getLoginCustomerRequest(
            userInputFields,
            queryParam,
            standalonePage
          )
        )
        .subscribe((response) => {
          if (response && !response?.errors) {
            this.setRmeUI(userInputFields.persistentLogin);
            this.pingAdapter.processLoginDashboardSuccessResponse(
              response,
              this.authenticationConfig,
              standalonePage
            );
            this.disableSpinnerForAccountRecoveryCustLogin(response);
          } else if (response?.errors) {
            const errorResponse: {
              errorId: string;
              accountLocked?: boolean;
            } = {
              errorId: `ERROR_MSG_${response?.errors[0]?.code}`
            };
            this.handleCustLoginErrorCode(errorResponse);
            this.updateLoaderAndDisableButton(false);
            this.displayPingError(errorResponse);
            this.errorAdobeTracker$.next(true);
          }
        });
    }
  }

  disableSpinnerForAccountRecoveryCustLogin(response: loginCustomerResponse) {
    if (!response.hasEmail || !response.isEmailValid || !response.hasSQA)
      this.updateLoaderAndDisableButton(false);
  }

  recoverAccountCheck(errorResponse: {
    errorId: string;
    accountLocked?: boolean | undefined;
  }) {
    return (
      errorResponse.errorId === 'ERROR_MSG_39' ||
      errorResponse.errorId === 'ERROR_MSG_139' ||
      errorResponse.errorId === 'ERROR_MSG_138'
    );
  }

  handleCustLoginErrorCode(errorResponse: {
    errorId: string;
    accountLocked?: boolean;
  }) {
    if (errorResponse.errorId === 'ERROR_MSG_30') {
      errorResponse.accountLocked = true;
    } else if (errorResponse.errorId === 'ERROR_MSG_38') {
      return window.location.replace(LOGIN_PAGE_ROUTES.CREATE_NEW_PWD);
    } else if (this.recoverAccountCheck(errorResponse)) {
      return window.location.replace(LOGIN_PAGE_ROUTES.RECOVER_ACCOUNT);
    }
    return errorResponse;
  }
}
